import React, { useState, useEffect, useRef, useCallback } from "react";

import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import { Box, Button, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import styled from "styled-components";

import {
  createNewDocument,
  getbyIDDocument,
  getDocumentByID,
  UpdateDocumentById,
} from "../../Api/Services/DocumentServices";
import { getPetNameByWorkspaceId } from "../../Api/Services/PetServices";
import { useAuth } from "../../ContextProvider/AuthProvider/AuthProvider";
import {
  FormHeaderBox,
  FormHeaderTitle,
  Body,
  Column,
  ButtonBoxStyle,
  CancelButton,
} from "../../Css";
import { DocumentFormValidation, EditDocumentFormValidation } from "../../From/Validation";
import { formatDate } from "../../Utils";
import AutoComplete from "../Common/AutoComplete";
import CustomButton from "../Common/Button";
import FormGroup from "../Common/FormGroup";
import FormLayout from "../Common/FormHeader";
import InputField from "../Common/InputField";
import { showSnackbar } from "../Common/Snackbar";
import UploadDocument from "../Common/UploadDocument";
import { useTheme } from "@mui/material/styles";
import { getCategoryDocument } from "../../../api/axios";

const defaultValue = {
  documment_name: "",
  date: "",
  documment: "",
  pet_id: "",
  category_id: "",
  sub_category_id: "",
};

const FormSection = () => {
  // Hooks
  const navigate = useNavigate();
  const { id } = useParams();
  const { petOwnerId: user_id, workspaceId: w_id } = useAuth();
  const theme = useTheme();
  const componentPDF = useRef();
  const inputRef = useRef(null);

  // State
  const [initialValue, setInitialValue] = useState(defaultValue);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [documentData, setDocumentData] = useState([]);

  // Determine if this is edit mode
  const isEditMode = !!id;

  // Memoized functions
  const getCurrentDate = useCallback(() => {
    const now = new Date();
    let month = (now.getMonth() + 1).toString();
    let day = now.getDate().toString();

    if (month.length === 1) {
      month = "0" + month;
    }

    if (day.length === 1) {
      day = "0" + day;
    }

    return `${now.getFullYear()}-${month}-${day}`;
  }, []);

  const handleCategoryChange = useCallback((selectedCategory) => {
    const category = categories.find(cat => cat.value === selectedCategory);
    if (category) {
      const subcategoryOptions = category?.subcategories?.map(sub => ({
        value: sub.id,
        label: sub.name
      }));
      setSubcategories(subcategoryOptions);
    } else {
      setSubcategories([]);
    }
  }, [categories]);

  const handleDivClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleDownload = useCallback(() => {
    window.open(documentData.documment);
  }, [documentData.documment]);

  // API functions
  const getPetNames = useCallback(async () => {
    try {
      const { data } = await getPetNameByWorkspaceId(w_id);
      const filteredData = data?.data?.map((item) => ({
        value: item?.id,
        label: item?.name,
      }));
      setItems(filteredData);
    } catch (error) {
      console.error("error: ", error);
    }
  }, [w_id]);

  const getCategories = useCallback(async () => {
    try {
      const { data } = await getCategoryDocument();
      const filteredData = data?.data?.map((item) => ({
        value: item?.id,
        label: item?.category,
        subcategories: item?.sub_category || []
      }));
      setCategories(filteredData);
    } catch (error) {
      console.error("error: ", error);
    }
  }, []);

  const loadDocumentDetails = useCallback(async () => {
    if (!isEditMode) return;
    
    try {
      const { data } = await getbyIDDocument(id);
      setInitialValue({
        ...defaultValue,
        documment_name: data?.data?.[0]?.documment_name,
        date: formatDate(data?.data?.[0]?.date),
        pet_id: data?.data?.[0]?.pet_id,
        category_id: data?.data?.[0]?.category_id,
        sub_category_id: data?.data?.[0]?.sub_category_id,
      });
    } catch (error) {
      console.error("error: ", error);
      showSnackbar({
        type: "error",
        message: "Failed to load document details.",
      });
    }
  }, [isEditMode, id]);

  const loadDocumentData = useCallback(async () => {
    if (!isEditMode) return;
    
    try {
      const { data } = await getDocumentByID(id);
      setDocumentData(data.data[0]);
    } catch (error) {
      console.error("error: ", error);
    }
  }, [isEditMode, id]);

  // Create new document
  const createDocumentData = useCallback(async (values) => {
    try {
      const formData = new FormData();
      formData.append("documment_name", values?.documment_name);
      formData.append("date", values?.date);
      formData.append("documment", values?.documment);
      formData.append("pet_id", values?.pet_id);
      formData.append("category_id", values?.category_id);
      formData.append("sub_category_id", values?.sub_category_id);
      formData.append("user_id", user_id);
      formData.append("w_id", w_id);

      await createNewDocument(formData);
      showSnackbar({
        type: "success",
        message: "Document information added successfully",
      });
    } catch (err) {
      showSnackbar({
        type: "error",
        message: "Document information added failed",
      });
      console.error(err);
      throw err;
    }
  }, [user_id, w_id]);

  // Update existing document
  const updateDocumentData = useCallback(async (values) => {
    try {
      const formData = new FormData();
      formData.append("documment_name", values?.documment_name);
      formData.append("date", values?.date);
      formData.append("documment", values?.documment);
      formData.append("pet_id", values?.pet_id);
      formData.append("user_id", user_id);
      formData.append("w_id", w_id);

      await UpdateDocumentById(id, formData);
      showSnackbar({
        type: "success",
        message: "Document information updated successfully",
      });
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Document information updated failed",
      });
      console.error("error: ", error);
      throw error;
    }
  }, [id, user_id, w_id]);

  // Form submission handler
  const handleSubmit = useCallback(async (values, { isSubmitting }) => {
    try {
      if (isEditMode) {
        await updateDocumentData(values);
      } else {
        await createDocumentData(values);
      }
      navigate(-1);
    } catch (error) {
      console.error("Failed to process document:", error);
    } finally {
      isSubmitting(false);
    }
  }, [isEditMode, updateDocumentData, createDocumentData, navigate]);

  // Print functionality
  const handlePrint = useReactToPrint({
    content: () => componentPDF.current,
    documentTitle: "Document Information",
    pageStyle: `
      @page { size: A3; margin: 200px; }
      body { margin: 100px; }
      .print-text-field { font-size: 14px; }
    `,
  });

  // Effects
  useEffect(() => {
    getPetNames();
    getCategories();
    if (isEditMode) {
      loadDocumentDetails();
      loadDocumentData();
    }
  }, [w_id, isEditMode, getPetNames, getCategories, loadDocumentDetails, loadDocumentData]);

  return (
    <FormLayout title={isEditMode ? "Edit Document" : "Document"}>
      <Formik
        initialValues={initialValue}
        onSubmit={handleSubmit}
        validationSchema={isEditMode ? EditDocumentFormValidation : DocumentFormValidation}
        enableReinitialize
        validateOnMount={false}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ getFieldProps, values, isSubmitting, setFieldValue, setFieldTouched, errors, touched }) => (
          <Form>
            <Box sx={FormHeaderBox(theme.palette.primary.main)}>
              <Box sx={{ borderBottom: `1px solid ${theme.palette.primary.main}`, width: "100%" }}>
                <Typography variant="h6" sx={FormHeaderTitle}>
                  Document Details
                </Typography>
              </Box>
              <div className="scroll2" ref={componentPDF} style={{ width: "100%" }}>
                <Body style={{ gap: "10px 20px" }}>
                  <Column>
                    <FormGroup name="pet_id" label={"Pet Name"} requiredField>
                      <AutoComplete
                        {...getFieldProps("pet_id")}
                        options={items}
                        value={isEditMode ? items.find(option => option.value === values.pet_id) : undefined}
                        textFieldStyle={{
                          width: {
                            xs: 250,
                            sm: 443,
                            md: 443,
                            lg: 443,
                            xl: 443,
                          },
                          height: "35px",
                        }}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="documment_name"
                      label={"Document Name"}
                      requiredField
                    >
                      <InputField {...getFieldProps("documment_name")} />
                    </FormGroup>
                  </Column>
                  {!isEditMode && (
                    <>
                      <Column>
                        <FormGroup name="category_id" label={"Category"} requiredField>
                          <AutoComplete
                            {...getFieldProps("category_id")}
                            options={categories}
                            onChange={(e, value) => {
                              setFieldValue("category_id", value?.value || "");
                              setFieldValue("sub_category_id", "");
                              handleCategoryChange(value?.value);
                            }}
                            isOnChange
                          />
                        </FormGroup>
                      </Column>
                      <Column>
                        <FormGroup name="sub_category_id" label={"Subcategory"}>
                          <AutoComplete
                            {...getFieldProps("sub_category_id")}
                            options={subcategories}
                          />
                        </FormGroup>
                      </Column>
                    </>
                  )}
                  <Column>
                    <FormGroup name="date" label={"Date"} requiredField>
                      <InputField
                        type="date"
                        {...getFieldProps("date")}
                        inputProps={{ max: getCurrentDate() }}
                      />
                    </FormGroup>
                  </Column>
                </Body>
                <Body style={{ gap: "10px 20px", marginTop: "10px" }}>
                  <Column>
                    <FormGroup
                      name="documment"
                      label={"Upload Documents"}
                      requiredField={!isEditMode}
                    >
                      {isEditMode ? (
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Input4 style={errors?.documment && touched.documment && { borderColor: "rgb(230, 0, 0)" }}>
                            <input
                              type="file"
                              ref={inputRef}
                              style={{ display: "none" }}
                              onChange={(event) => {
                                const file = event.target.files[0];
                                setFieldTouched("documment", true);
                                setFieldValue("documment", file);
                              }}
                            />
                            {values?.documment ? (
                              <>
                                {values?.documment && (
                                  <div style={{ color: "black" }}>
                                    <p>{values?.documment?.name}</p>
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <object
                                  data={documentData.documment}
                                  type="application/pdf"
                                  width="100%"
                                  height="95%"
                                > </object>
                              </>
                            )}
                          </Input4>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                            }}
                          >
                            <Button
                              style={{
                                color: "white",
                                marginTop: "10px",
                                background: '#1c93ed'
                              }}
                              onClick={handleDivClick}
                            >
                              Upload New Document
                            </Button>
                            <Button
                              sx={{
                                color: "white",
                                marginLeft: { xs: "0px", sm: "80px" },
                                marginTop: "10px",
                                background: '#1c93ed',
                                '&:hover': {
                                  background: '#1c93ed'
                                }
                              }}
                              onClick={handleDownload}
                            >
                              View & download
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <UploadDocument {...getFieldProps("documment")} />
                      )}
                    </FormGroup>
                  </Column>
                </Body>
              </div>
              <Box sx={ButtonBoxStyle(`${theme.palette.primary.main}`)}>
                <Box display="flex">
                  <CustomButton
                    label="Cancel"
                    type="button"
                    style={CancelButton(`${theme.palette.primary.main}`)}
                    onClick={() => navigate(-1)}
                  />
                  <CustomButton
                    label={isSubmitting ? (isEditMode ? "Saving..." : "Submitting...") : (isEditMode ? "Save" : "Submit")}
                    disabled={isSubmitting}
                    type="submit"
                  />
                  {isEditMode && (
                    <CustomButton
                      startIcon={<LocalPrintshopOutlinedIcon />}
                      onClick={handlePrint}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </FormLayout>
  );
};

const Input4 = styled.div`
  width: calc(100% - 20px);
  height: 180px;
  background: var(--primary-color);
  border: 2px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.25);
  color: #ffffff;
  backdrop-filter: blur(10px);
  border-radius: 11px;
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 10px;
  cursor: pointer;
`;

export default FormSection;
